import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { DELAYS } from '../utils/constants';
import { useBoardSection } from './BoardSectionContext';

// Separate contexts for state and actions to minimize rerenders
const FilterStateContext = createContext(null);
const FilterActionsContext = createContext(null);

/**
 * Debounce utility for search input
 * @param {Function} func - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export const FilterProvider = ({ children }) => {
  const { userDetails } = useUserDetailsContext();
  const { isGlobal } = useBoardSection();

  // Search keyword with debouncing
  const [searchInput, setSearchInput] = useState(''); // Immediate input value
  const [searchKeyword, setSearchKeyword] = useState(''); // Debounced value for API calls

  const [practiceArea, setPracticeArea] = useState(isGlobal ? userDetails?.user_preferences?.filters?.tasks?.practice_area || '' : '');
  const [assignedTo, setAssignedTo] = useState(isGlobal ? userDetails?.user_preferences?.filters?.tasks?.assigned_to || [] : []);
  const [sortType, setSortType] = useState(isGlobal ? userDetails?.user_preferences?.filters?.tasks?.sort_by : null);
  const [dateFilter, setDateFilter] = useState(isGlobal ? userDetails?.user_preferences?.filters?.tasks?.date : null);
  const [priority, setPriority] = useState(isGlobal ? userDetails?.user_preferences?.filters?.tasks?.priority : null);

  // Create debounced search function
  const debouncedSetSearchKeyword = useRef(
    debounce((value) => {
      setSearchKeyword(value);
    }, DELAYS.DEBOUNCE_SEARCH)
  ).current;

  // Update debounced search when input changes
  useEffect(() => {
    debouncedSetSearchKeyword(searchInput);
  }, [searchInput, debouncedSetSearchKeyword]);

  // Memoize actions (stable references - never cause rerenders)
  const actions = useMemo(
    () => ({
      setSearchInput,
      setPracticeArea,
      setAssignedTo,
      setSortType,
      setDateFilter,
      setPriority,
      clearFilters: () => {
        setSearchInput('');
        setSearchKeyword('');
        setPracticeArea('');
        setAssignedTo([]);
        setSortType(null);
        setDateFilter(null);
        setPriority(null);
      },
    }),
    [] // Empty deps - actions are stable
  );

  // Memoize state (only rerenders when actual filter values change)
  const state = useMemo(() => {
    const hasActiveFilters = !!(searchKeyword || practiceArea || assignedTo?.length > 0 || sortType || dateFilter || priority);

    return {
      searchInput, // For controlled input component
      searchKeyword, // For API calls
      practiceArea,
      assignedTo,
      sortType,
      dateFilter,
      priority,
      hasActiveFilters,
    };
  }, [searchInput, searchKeyword, practiceArea, assignedTo, sortType, dateFilter, priority]);

  return (
    <FilterActionsContext.Provider value={actions}>
      <FilterStateContext.Provider value={state}>{children}</FilterStateContext.Provider>
    </FilterActionsContext.Provider>
  );
};

/**
 * Hook to access filter state (only rerenders when state changes)
 */
export const useFilterState = () => {
  const context = useContext(FilterStateContext);
  if (!context) {
    throw new Error('useFilterState must be used within FilterProvider');
  }
  return context;
};

/**
 * Hook to access filter actions (never causes rerenders)
 */
export const useFilterActions = () => {
  const context = useContext(FilterActionsContext);
  if (!context) {
    throw new Error('useFilterActions must be used within FilterProvider');
  }
  return context;
};

/**
 * Legacy hook for backward compatibility
 * @deprecated Use useFilterState and useFilterActions instead
 */
export const useFilters = () => {
  const state = useFilterState();
  const actions = useFilterActions();

  return {
    ...state,
    ...actions,
  };
};
